import { FC, ReactNode, useMemo } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import classNames from "classnames";

import { DataId } from "../DataId";
import { Handle } from "./Handle";
import { ItemContent } from "./ItemContent";

import styles from "./styles.module.css";

/** The `Item` component properties. */
interface ItemProps {
  /** The list item ID. */
  readonly id: DataId;

  /** The item children. */
  readonly children?: ReactNode;
}

/** An individual draggable item within the orderable list. */
export const Item: FC<ItemProps> = (props) => {
  const { id, children } = props;
  const { setNodeRef, setActivatorNodeRef, isDragging, listeners, transform, transition, attributes } = useSortable({
    id: id.toString(),
    transition: { duration: 0, easing: "linear" },
  });

  const style = useMemo(() => {
    return {
      transform: CSS.Translate.toString(transform),
      transition,
    };
  }, [transform, transition]);

  const { role: _role, tabIndex: _tabIndex, "aria-disabled": _ariaDisabled, ...restAttributes } = attributes;

  return (
    <li
      ref={setNodeRef}
      className={classNames(styles.Item, "item", { dragging: isDragging })}
      style={style}
      {...restAttributes}
      aria-roledescription="sortable item"
    >
      <Shelf spacing="small">
        <Handle aria-label="Rearrange element" ref={setActivatorNodeRef} {...listeners} />
        <ItemContent>{children}</ItemContent>
      </Shelf>
    </li>
  );
};
